// src/writer/components/SelectiveGenerationInterface.tsx
import React, { useState, useEffect } from 'react';
import { ProjectState } from '../types/project';
import { BookStructureElementKey, FrontMatter, BackMatter, AuthorDetails } from '../types';
import { primaryButtonClass, actionButtonClass, secondaryButtonClass } from '../constants';
import { useElementSelectionPreferences } from '../hooks/useElementSelectionPreferences';

interface GenerationSelection {
  frontMatter: {
    [key in keyof Pick<FrontMatter, 'titlePageContent' | 'copyrightPageContent' | 'dedication' | 'epigraph' | 'tableOfContents' | 'foreword' | 'forewordBy' | 'preface' | 'acknowledgmentsFront' | 'prologue' | 'introduction' | 'titleImagePrompt'>]?: boolean;
  };
  backMatter: {
    [key in keyof Pick<BackMatter, 'epilogue' | 'afterword' | 'authorsNote' | 'acknowledgmentsBack' | 'aboutTheAuthor' | 'readingGroupGuide' | 'teaserNextBook' | 'index' | 'backCoverImagePrompt'>]?: boolean;
  };
  authorDetails: {
    [key in keyof Pick<AuthorDetails, 'isbn' | 'publisher' | 'edition' | 'copyrightNotice'>]?: boolean;
  };
}

interface SelectiveGenerationInterfaceProps {
  projectState: ProjectState;
  onGenerateSelected: (selectedElements: BookStructureElementKey[]) => void;
  isGenerating: boolean;
  isModal?: boolean; // New prop to indicate if this is being used in a modal
}

const SelectiveGenerationInterface: React.FC<SelectiveGenerationInterfaceProps> = ({
  projectState,
  onGenerateSelected,
  isGenerating,
  isModal = false
}) => {
  // Use persistent preferences hook
  const {
    preferences: selections,
    updateElementPreference,
    updateCategoryPreferences,
    resetToDefaults,
    getSelectedCount: getStoredSelectedCount
  } = useElementSelectionPreferences(projectState);
  const [expandedSections, setExpandedSections] = useState({
    frontMatter: true,
    backMatter: true,
    authorDetails: false
  });

  // Check what's already generated to show status
  const getElementStatus = (category: keyof GenerationSelection, key: string): 'generated' | 'empty' | 'partial' => {
    const categoryData = projectState[category];
    if (!categoryData) return 'empty';

    const value = categoryData[key as keyof typeof categoryData];
    if (value && String(value).trim() !== '') {
      return 'generated';
    }
    return 'empty';
  };

  // Get count of selected elements (use hook's implementation)
  const getSelectedCount = getStoredSelectedCount;

  // Handle element selection
  const handleElementToggle = (category: keyof GenerationSelection, key: string) => {
    const currentValue = selections[category][key as keyof typeof selections[typeof category]];
    updateElementPreference(category, key, !currentValue);
  };

  // Handle section expand/collapse
  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Select/Deselect all in a category
  const handleCategoryToggle = (category: keyof GenerationSelection, selectAll: boolean) => {
    const updatedCategory = { ...selections[category] };
    Object.keys(updatedCategory).forEach(key => {
      (updatedCategory as any)[key as keyof typeof updatedCategory] = selectAll;
    });
    updateCategoryPreferences(category, updatedCategory);
  };

  // Master select/deselect all
  const handleSelectAll = () => {
    // Set all categories to all true
    const frontMatterAll: any = {};
    Object.keys(selections.frontMatter).forEach(key => {
      frontMatterAll[key] = true;
    });
    updateCategoryPreferences('frontMatter', frontMatterAll);

    const backMatterAll: any = {};
    Object.keys(selections.backMatter).forEach(key => {
      backMatterAll[key] = true;
    });
    updateCategoryPreferences('backMatter', backMatterAll);

    const authorDetailsAll: any = {};
    Object.keys(selections.authorDetails).forEach(key => {
      authorDetailsAll[key] = true;
    });
    updateCategoryPreferences('authorDetails', authorDetailsAll);
  };

  const handleDeselectAll = () => {
    // Set all categories to all false
    const frontMatterNone: any = {};
    Object.keys(selections.frontMatter).forEach(key => {
      frontMatterNone[key] = false;
    });
    updateCategoryPreferences('frontMatter', frontMatterNone);

    const backMatterNone: any = {};
    Object.keys(selections.backMatter).forEach(key => {
      backMatterNone[key] = false;
    });
    updateCategoryPreferences('backMatter', backMatterNone);

    const authorDetailsNone: any = {};
    Object.keys(selections.authorDetails).forEach(key => {
      authorDetailsNone[key] = false;
    });
    updateCategoryPreferences('authorDetails', authorDetailsNone);
  };

  // Generate selected elements
  const handleGenerate = () => {
    const selectedElements: BookStructureElementKey[] = [];

    // Collect selected front matter elements
    Object.entries(selections.frontMatter).forEach(([key, selected]) => {
      if (selected) {
        selectedElements.push(key as BookStructureElementKey);
      }
    });

    // Collect selected back matter elements
    Object.entries(selections.backMatter).forEach(([key, selected]) => {
      if (selected) {
        selectedElements.push(key as BookStructureElementKey);
      }
    });

    // Collect selected author details elements
    Object.entries(selections.authorDetails).forEach(([key, selected]) => {
      if (selected) {
        selectedElements.push(key as BookStructureElementKey);
      }
    });

    onGenerateSelected(selectedElements);
  };

  // Element display names
  const elementDisplayNames: Record<string, string> = {
    // Front Matter
    titlePageContent: 'Title Page Content',
    copyrightPageContent: 'Copyright Page Content',
    dedication: 'Dedication',
    epigraph: 'Epigraph',
    tableOfContents: 'Table of Contents',
    foreword: 'Foreword',
    forewordBy: 'Foreword By',
    preface: 'Preface',
    acknowledgmentsFront: 'Acknowledgments (Front)',
    prologue: 'Prologue',
    introduction: 'Introduction',
    titleImagePrompt: 'Title Image Description',
    // Back Matter
    epilogue: 'Epilogue',
    afterword: 'Afterword',
    authorsNote: "Author's Note",
    acknowledgmentsBack: 'Acknowledgments (Back)',
    aboutTheAuthor: 'About the Author',
    readingGroupGuide: 'Reading Group Guide',
    teaserNextBook: 'Teaser Next Book',
    index: 'Index',
    backCoverImagePrompt: 'Back Cover Image Description',
    // Author Details
    isbn: 'ISBN',
    publisher: 'Publisher',
    edition: 'Edition',
    copyrightNotice: 'Copyright Notice'
  };

  // Status icons
  const getStatusIcon = (status: 'generated' | 'empty' | 'partial') => {
    switch (status) {
      case 'generated': return '✅';
      case 'partial': return '⚠️';
      case 'empty': return '⭕';
      default: return '⭕';
    }
  };

  const selectedCount = getSelectedCount();

  return (
    <div className={isModal ? "bg-transparent" : "bg-white rounded-xl shadow-lg p-6"}>
      {!isModal && (
        <div className="flex justify-between items-center mb-6">
          <h4 className="text-lg font-semibold text-gray-800">✨ Selective AI Generation</h4>
        <div className="flex space-x-2">
          <button
            onClick={handleSelectAll}
            className={`${secondaryButtonClass} text-sm py-2 px-3`}
          >
            Select All
          </button>
          <button
            onClick={handleDeselectAll}
            className={`${secondaryButtonClass} text-sm py-2 px-3`}
          >
            Deselect All
          </button>
        </div>
        </div>
        )}

      {/* Always show select/deselect buttons, even in modal */}
      {isModal && (
        <div className="flex justify-end space-x-2 mb-6">
          <button
            onClick={handleSelectAll}
            className={`${secondaryButtonClass} text-sm py-2 px-3`}
          >
            Select All
          </button>
          <button
            onClick={handleDeselectAll}
            className={`${secondaryButtonClass} text-sm py-2 px-3`}
          >
            Deselect All
          </button>
        </div>
      )}

      <div className="space-y-4">
        {/* Front Matter Section */}
        <div className="border rounded-lg p-4">
          <div
            className="flex items-center justify-between cursor-pointer"
            onClick={() => toggleSection('frontMatter')}
          >
            <div className="flex items-center space-x-2">
              <span className="text-lg">📖</span>
              <h5 className="font-medium text-gray-800">Front Matter</h5>
              <span className="text-sm text-gray-500">
                ({Object.values(selections.frontMatter).filter(Boolean).length} selected)
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCategoryToggle('frontMatter', true);
                }}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                All
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCategoryToggle('frontMatter', false);
                }}
                className="text-xs text-gray-600 hover:text-gray-800"
              >
                None
              </button>
              <span className="text-gray-400">
                {expandedSections.frontMatter ? '▼' : '▶'}
              </span>
            </div>
          </div>

          {expandedSections.frontMatter && (
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
              {Object.keys(selections.frontMatter).map(key => {
                const status = getElementStatus('frontMatter', key);
                const isSelected = selections.frontMatter[key as keyof typeof selections.frontMatter];
                return (
                  <label key={key} className={`flex items-center space-x-2 p-2 rounded cursor-pointer hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}>
                    <input
                      type="checkbox"
                      checked={isSelected || false}
                      onChange={() => handleElementToggle('frontMatter', key)}
                      className="rounded"
                    />
                    <span className="text-sm">{getStatusIcon(status)}</span>
                    <span className="text-sm flex-grow">{elementDisplayNames[key]}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* Back Matter Section */}
        <div className="border rounded-lg p-4">
          <div
            className="flex items-center justify-between cursor-pointer"
            onClick={() => toggleSection('backMatter')}
          >
            <div className="flex items-center space-x-2">
              <span className="text-lg">📚</span>
              <h5 className="font-medium text-gray-800">Back Matter</h5>
              <span className="text-sm text-gray-500">
                ({Object.values(selections.backMatter).filter(Boolean).length} selected)
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCategoryToggle('backMatter', true);
                }}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                All
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCategoryToggle('backMatter', false);
                }}
                className="text-xs text-gray-600 hover:text-gray-800"
              >
                None
              </button>
              <span className="text-gray-400">
                {expandedSections.backMatter ? '▼' : '▶'}
              </span>
            </div>
          </div>

          {expandedSections.backMatter && (
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
              {Object.keys(selections.backMatter).map(key => {
                const status = getElementStatus('backMatter', key);
                const isSelected = selections.backMatter[key as keyof typeof selections.backMatter];
                return (
                  <label key={key} className={`flex items-center space-x-2 p-2 rounded cursor-pointer hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}>
                    <input
                      type="checkbox"
                      checked={isSelected || false}
                      onChange={() => handleElementToggle('backMatter', key)}
                      className="rounded"
                    />
                    <span className="text-sm">{getStatusIcon(status)}</span>
                    <span className="text-sm flex-grow">{elementDisplayNames[key]}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* Author Details Section */}
        <div className="border rounded-lg p-4">
          <div
            className="flex items-center justify-between cursor-pointer"
            onClick={() => toggleSection('authorDetails')}
          >
            <div className="flex items-center space-x-2">
              <span className="text-lg">👤</span>
              <h5 className="font-medium text-gray-800">Publishing Info</h5>
              <span className="text-sm text-gray-500">
                ({Object.values(selections.authorDetails).filter(Boolean).length} selected)
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCategoryToggle('authorDetails', true);
                }}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                All
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCategoryToggle('authorDetails', false);
                }}
                className="text-xs text-gray-600 hover:text-gray-800"
              >
                None
              </button>
              <span className="text-gray-400">
                {expandedSections.authorDetails ? '▼' : '▶'}
              </span>
            </div>
          </div>

          {expandedSections.authorDetails && (
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
              {Object.keys(selections.authorDetails).map(key => {
                const status = getElementStatus('authorDetails', key);
                const isSelected = selections.authorDetails[key as keyof typeof selections.authorDetails];
                return (
                  <label key={key} className={`flex items-center space-x-2 p-2 rounded cursor-pointer hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}>
                    <input
                      type="checkbox"
                      checked={isSelected || false}
                      onChange={() => handleElementToggle('authorDetails', key)}
                      className="rounded"
                    />
                    <span className="text-sm">{getStatusIcon(status)}</span>
                    <span className="text-sm flex-grow">{elementDisplayNames[key]}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

      </div>

      {/* Legend */}
      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
        <div className="text-xs text-gray-600 space-y-1">
          <div className="font-medium mb-2">Status Legend:</div>
          <div className="flex items-center space-x-4">
            <span>✅ Generated</span>
            <span>⭕ Not Generated</span>
            <span>⚠️ Partial</span>
          </div>
        </div>
      </div>

      {/* Generate Button */}
      <div className="mt-6 pt-4 border-t">
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-600">
            {selectedCount} element{selectedCount !== 1 ? 's' : ''} selected
          </div>
          <button
            onClick={handleGenerate}
            disabled={isGenerating || selectedCount === 0}
            className={`${actionButtonClass} py-3 px-6 ${isGenerating || selectedCount === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {isGenerating ? (
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Generating {selectedCount} Elements...</span>
              </div>
            ) : (
              `✨ Generate Selected (${selectedCount})`
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SelectiveGenerationInterface;